import React, { useState, useEffect } from 'react';
import {
  InputPageQuestion,
  InputPageQuestionText,
  InputPageTitle,
  InputPageSubtitle,
  InputPageWrapper,
  SubmitButton,
  SubmitButtonWrapper,
  IncentivesSection,
  IncentiveTitle,
  IncentivesDescription,
  IncentiveInputContainer,
  IncentiveInput,
  InputDescription,
  ValidationTooltip,
  ValidationTooltipTitle,
  ValidationTooltipList,
  ValidationTooltipItem,
  MobileValidationSection,
  WorkplaceEvCount,
  WorkplaceEvCountTitle,
  WorkplaceEvCountSection,
  WorkplaceEvCountSectionTitle,
  WorkplaceEvCountValue,
  VerticalSpacer,
  QuestionLabelRow,
  SectionRow,
  CaasWarningText,
} from './InputPage.styled';
import InputButtonGroup from '../../components/InputButtonGroup';
import InfoTooltip from '../../components/InfoTooltip';
import InputSelect from '../../components/InputSelect';
import InputText from '../../components/InputText';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { populateInputs } from '../../features/inputs/inputs';
import PageLayout from '../../components/PageLayout/PageLayout';
import chargers from '../../assets/sideImage.jpg';
import { useSubmitInputs } from '../../hooks/useSubmitInputs';
import InputSlider from '../../components/InputSlider';
import {
  US_STATES,
  ownershipModelOptions,
  equipmentTypeOptions,
  paymentTypeOptions,
  publicChargingOptions,
  chargingAtHomeOptions,
} from '../../features/inputs/inputOptions';

const getSuggestedChargingPorts = (parkingSpaces, existingChargers) => {
  const suggested =
    Math.min(100, Math.round(parkingSpaces * 0.1)) - (existingChargers ?? 0);
  return Math.max(1, suggested);
};

const InputPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const inputs = useSelector((state) => state.inputs.value);
  const validInputs = useSelector((state) => state.inputs.valid);
  const invalidMessages = useSelector((state) => state.inputs.invalidMessages);
  const invalidFields = useSelector((state) => state.inputs.invalidFields);
  const dispatch = useDispatch();
  const { submitInputs, isSubmitting } = useSubmitInputs();
  const isNonNetworked = inputs.equipmentType.includes('Non-networked');
  const [showValidationTooltip, setShowValidationTooltip] =
    React.useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const [hasReachedBottom, setHasReachedBottom] = useState(false);
  const [usingSuggestedPorts, setUsingSuggestedPorts] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      const buffer = 50;
      const scrolledToBottom =
        window.innerHeight + window.scrollY >=
        document.body.scrollHeight - buffer;
      setIsAtBottom(scrolledToBottom);
      if (scrolledToBottom) setHasReachedBottom(true);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (inputs.chargingPorts !== null) setUsingSuggestedPorts(false);
  }, []);

  useEffect(() => {
    if (location.state?.scrollToBottom) {
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const fieldKeyToId = {
    numOfEmployees: 'question-numOfEmployees',
    avgDailyAttenance: 'question-avgDailyAttenance',
    carShare: 'question-carShare',
    evShare: 'question-evShare',
    state: 'question-address',
    parkingSpaces: 'question-parkingSpaces',
    existingChargers: 'question-existingChargers',
    chargingPorts: 'question-chargingPorts',
    ownershipModel: 'question-ownershipModel',
    equipmentType: 'question-equipmentType',
    chargingType: 'question-chargingType',
    openToPublic: 'question-openToPublic',
    chargingAtHome: 'question-chargingAtHome',
    stateIncentive: 'question-stateIncentive',
    utilityIncentive: 'question-utilityIncentive',
  };

  const handleValidationClick = () => {
    const firstField = invalidFields[0];
    if (!firstField) return;
    const element = document.getElementById(fieldKeyToId[firstField]);
    if (!element) return;
    const headerOffset = 96;
    const elementTop = element.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({ top: elementTop - headerOffset, behavior: 'smooth' });

    setTimeout(() => {
      const observer = new IntersectionObserver(
        (entries, obs) => {
          if (entries[0].isIntersecting) {
            obs.disconnect();
            element.classList.add('flash');
            element.addEventListener(
              'animationend',
              () => element.classList.remove('flash'),
              { once: true },
            );
          }
        },
        { threshold: 0.3 },
      );
      observer.observe(element);
    }, 50);
  };

  const submit = () => {
    dispatch(populateInputs({ ...inputs }));
    if (validInputs) {
      submitInputs(
        { ...inputs },
        {
          onSuccess: () => {
            navigate('/analysis');
          },
          onError: (error) => {
            console.error(error);
          },
        },
      );
    }
  };

  const dailyAttendance = inputs.numOfEmployees
    ? Math.round(inputs.numOfEmployees * (inputs.avgDailyAttenance / 100))
    : '—';
  const employeeCarCommuters =
    typeof dailyAttendance === 'number'
      ? Math.round(dailyAttendance * (inputs.carShare / 100))
      : '—';
  const evCommuters =
    typeof employeeCarCommuters === 'number'
      ? Math.round(employeeCarCommuters * (inputs.evShare / 100))
      : '—';

  const renderCostDisabledMessage = () => {
    if (isNonNetworked)
      return 'Non-networked chargers do not support paid employee charging';
    if (inputs.ownershipModel === 'Charging-as-a-Service')
      return 'Pricing model set by the charging service provider';
    return;
  };

  return (
    <PageLayout sideImage={{ src: chargers, alt: 'Image of EV Chargers' }}>
      <InputPageWrapper $showValidation={hasReachedBottom && !validInputs}>
        <InputPageTitle>
          Tell us about your workplace
          <InputPageSubtitle>
            Please note all fields are required.
          </InputPageSubtitle>
        </InputPageTitle>

        <WorkplaceEvCount $row={2} $column={5} $colSpan={2} $rowSpan={2}>
          <WorkplaceEvCountTitle>Workplace EV Count</WorkplaceEvCountTitle>

          <WorkplaceEvCountSection>
            <WorkplaceEvCountSectionTitle>
              Daily Attendance
            </WorkplaceEvCountSectionTitle>
            <WorkplaceEvCountValue>{dailyAttendance}</WorkplaceEvCountValue>
          </WorkplaceEvCountSection>

          <WorkplaceEvCountSection>
            <WorkplaceEvCountSectionTitle>
              Employee Car Commuters
            </WorkplaceEvCountSectionTitle>
            <WorkplaceEvCountValue>
              {employeeCarCommuters}
            </WorkplaceEvCountValue>
          </WorkplaceEvCountSection>

          <WorkplaceEvCountSection>
            <WorkplaceEvCountSectionTitle>
              EV Commuters
            </WorkplaceEvCountSectionTitle>
            <WorkplaceEvCountValue>{evCommuters}</WorkplaceEvCountValue>
          </WorkplaceEvCountSection>
        </WorkplaceEvCount>

        <InputPageQuestion
          id="question-numOfEmployees"
          $row={2}
          $column={1}
          $colSpan={2}
        >
          <InputPageQuestionText>Total Employees</InputPageQuestionText>
          <InputText
            id="numOfEmployees"
            value={inputs.numOfEmployees}
            onChange={(val) =>
              dispatch(populateInputs({ ...inputs, numOfEmployees: val }))
            }
          />
        </InputPageQuestion>
        <InputPageQuestion
          id="question-avgDailyAttenance"
          $row={2}
          $column={3}
          $colSpan={2}
        >
          <InputPageQuestionText>Avg Daily Attendance</InputPageQuestionText>
          <InputSlider
            step={5}
            min={0}
            max={100}
            value={inputs.avgDailyAttenance}
            onChange={(e) => {
              dispatch(
                populateInputs({
                  ...inputs,
                  avgDailyAttenance: Number(e.target.value),
                }),
              );
            }}
            showMinMax
            valueDisplay="tracking-above"
            suffix="%"
            filledTrack={false}
          />
        </InputPageQuestion>

        <InputPageQuestion
          id="question-carShare"
          $row={3}
          $column={1}
          $colSpan={2}
        >
          <QuestionLabelRow>
            <InputPageQuestionText>Car Commuter Share</InputPageQuestionText>
            <InfoTooltip content="The percentage of employees who typically commute by car." />
          </QuestionLabelRow>
          <InputSlider
            step={5}
            min={0}
            max={100}
            value={inputs.carShare}
            onChange={(e) => {
              dispatch(
                populateInputs({
                  ...inputs,
                  carShare: Number(e.target.value),
                }),
              );
            }}
            showMinMax
            valueDisplay="tracking-above"
            suffix="%"
            filledTrack={false}
          />
        </InputPageQuestion>
        <InputPageQuestion
          id="question-evShare"
          $row={3}
          $column={3}
          $colSpan={2}
        >
          <QuestionLabelRow>
            <InputPageQuestionText>EV Commuter Share</InputPageQuestionText>
            <InfoTooltip content="The percentage of car commuters who drive EVs." />
          </QuestionLabelRow>
          <InputSlider
            step={5}
            min={0}
            max={100}
            value={inputs.evShare}
            onChange={(e) => {
              dispatch(
                populateInputs({
                  ...inputs,
                  evShare: Number(e.target.value),
                }),
              );
            }}
            showMinMax
            valueDisplay="tracking-above"
            suffix="%"
            filledTrack={false}
          />
        </InputPageQuestion>

        <VerticalSpacer $row={4} />

        <SectionRow $row={5}>
          <InputPageQuestion
            id="question-address"
            $row={1}
            $column={1}
            $colSpan={2}
            $midColumn={1}
            $midColSpan={3}
          >
            <InputPageQuestionText>State</InputPageQuestionText>
            <InputSelect
              options={US_STATES}
              value={inputs.state}
              onChange={(option) =>
                dispatch(populateInputs({ ...inputs, state: option.value }))
              }
            />
          </InputPageQuestion>

          <InputPageQuestion
            id="question-parkingSpaces"
            $row={1}
            $column={3}
            $colSpan={2}
            $midColumn={4}
            $midColSpan={3}
          >
            <InputPageQuestionText>Total Parking Spaces</InputPageQuestionText>
            <InputText
              id="parkingSpaces"
              value={inputs.parkingSpaces}
              onChange={(val) => {
                const update = { ...inputs, parkingSpaces: val };

                if (usingSuggestedPorts && val !== null) {
                  update.chargingPorts = getSuggestedChargingPorts(
                    val,
                    inputs.existingChargers,
                  );
                }
                dispatch(populateInputs(update));
              }}
            />
          </InputPageQuestion>

          <InputPageQuestion
            id="question-existingChargers"
            $row={1}
            $column={5}
            $colSpan={2}
            $midRow={2}
            $midColumn={1}
            $midColSpan={3}
          >
            <QuestionLabelRow>
              <InputPageQuestionText>
                Existing Charging Ports
              </InputPageQuestionText>
              <InfoTooltip content="The number of charging plugs or outlets that can be in use simultaneously across all chargers on-site." />
            </QuestionLabelRow>
            <InputText
              id="existingChargers"
              value={inputs.existingChargers}
              onChange={(val) => {
                const update = { ...inputs, existingChargers: val };
                if (usingSuggestedPorts && val !== null) {
                  update.chargingPorts = getSuggestedChargingPorts(
                    inputs.parkingSpaces,
                    val,
                  );
                }
                dispatch(populateInputs(update));
              }}
            />
          </InputPageQuestion>
        </SectionRow>

        <InputPageTitle $row={6}>
          Tell us about your project
          <InputPageSubtitle>
            Please note all fields are required.
          </InputPageSubtitle>
        </InputPageTitle>

        <SectionRow $row={7}>
          <InputPageQuestion
            id="question-chargingPorts"
            $row={1}
            $column={1}
            $midColumn={1}
            $midColSpan={3}
          >
            <QuestionLabelRow>
              <InputPageQuestionText>
                Charging Ports to Install
              </InputPageQuestionText>
              <InfoTooltip content="Enter the total number of charging ports you plan to install. Each port can charge one vehicle at a time." />
            </QuestionLabelRow>
            <InputText
              id="chargingPorts"
              value={inputs.chargingPorts}
              onChange={(val) => {
                if (usingSuggestedPorts) setUsingSuggestedPorts(false);
                dispatch(populateInputs({ ...inputs, chargingPorts: val }));
              }}
            />
          </InputPageQuestion>

          <InputPageQuestion
            id="question-ownershipModel"
            $row={1}
            $column={3}
            $midRow={2}
            $midColumn={1}
            $midColSpan={3}
          >
            <InputPageQuestionText>Ownership Model</InputPageQuestionText>
            <InputSelect
              options={ownershipModelOptions}
              value={inputs.ownershipModel}
              onChange={(option) => {
                const update = { ...inputs, ownershipModel: option.value };
                if (option.value === 'Charging-as-a-Service') {
                  update.equipmentType = 'Level 2 - Networked';
                  update.chargingType = 'Paid';
                }
                dispatch(populateInputs(update));
              }}
            />
          </InputPageQuestion>

          <InputPageQuestion
            id="question-equipmentType"
            $row={1}
            $column={5}
            $midRow={2}
            $midColumn={4}
            $midColSpan={3}
          >
            <QuestionLabelRow>
              <InputPageQuestionText>Charger Type</InputPageQuestionText>
              <InfoTooltip content="Please select the equipment type that is expected to be used: Level 1 chargers are 120 volt circuits that provide ~1.5-1.8kW of power. Level 2 chargers use 208 or 240 volt circuits that provide between 2.4 and 19.2 kW. They usually provide between 6-11 kW of power. Level two chargers can be networked or non-networked which will impact expected costs." />
            </QuestionLabelRow>
            <InputSelect
              options={equipmentTypeOptions}
              value={inputs.equipmentType}
              isDisabled={inputs.ownershipModel === 'Charging-as-a-Service'}
              disabledReason="Locked to Level 2 - Networked when Charging-as-a-Service (CaaS) is selected"
              onChange={(option) =>
                dispatch(
                  populateInputs({ ...inputs, equipmentType: option.value }),
                )
              }
            />
          </InputPageQuestion>
        </SectionRow>

        <InputPageQuestion
          id="question-chargingType"
          $row={8}
          $column={1}
          $colSpan={3}
        >
          <InputPageQuestionText>Employee Charging Cost</InputPageQuestionText>
          <InputButtonGroup
            options={paymentTypeOptions}
            value={inputs.chargingType}
            isDisabled={
              isNonNetworked ||
              inputs.ownershipModel === 'Charging-as-a-Service'
            }
            disabledReason={renderCostDisabledMessage()}
            onChange={(selected) =>
              dispatch(populateInputs({ ...inputs, chargingType: selected }))
            }
          />
        </InputPageQuestion>
        <InputPageQuestion
          id="question-openToPublic"
          $row={8}
          $column={4}
          $colSpan={3}
          $paddingLeft="2rem"
        >
          <InputPageQuestionText>Open to the Public</InputPageQuestionText>
          <InputButtonGroup
            options={publicChargingOptions}
            value={inputs.openToPublic}
            onChange={(selected) =>
              dispatch(populateInputs({ ...inputs, openToPublic: selected }))
            }
          />
        </InputPageQuestion>

        <InputPageQuestion
          id="question-chargingAtHome"
          $row={9}
          $column={1}
          $colSpan={3}
        >
          <InputPageQuestionText>
            Employees with Home Charging
          </InputPageQuestionText>
          <InputSelect
            options={chargingAtHomeOptions}
            value={inputs.chargingAtHome}
            onChange={(option) =>
              dispatch(
                populateInputs({ ...inputs, chargingAtHome: option.value }),
              )
            }
          />
        </InputPageQuestion>

        <IncentivesSection $row={10} $column={1} $colSpan={6}>
          <IncentiveTitle>Incentives</IncentiveTitle>
          <IncentivesDescription>
            Many states, federal programs, and utilities offer incentives to
            support workplace charging initiatives. Below, you may enter the
            incentive amount you expect to receive. This value will be included
            in your total cost calculation.
            <br />
            <br />
            To search for state incentives, start by checking with the{' '}
            <a href="https://afdc.energy.gov/laws/state">
              Alternative Fuels Data Center
            </a>{' '}
            and evaluating any relevant incentives for when they are accepting
            applications and potential incentive amounts. For any local electric
            utility incentives, you should search online for any relevant
            make-ready, fleet (if relevant), or business incentives available.
          </IncentivesDescription>
          {inputs.ownershipModel === 'Charging-as-a-Service' && (
            <CaasWarningText>
              Please check eligibility criteria for any incentive programs your
              project may pursue.
            </CaasWarningText>
          )}
          <IncentiveInputContainer>
            <IncentiveInput id="question-stateIncentive">
              <InputPageQuestionText>
                State Incentive Amount
              </InputPageQuestionText>
              <InputText
                id="stateIncentive"
                prefix={'$'}
                value={inputs.stateIncentive}
                onChange={(val) =>
                  dispatch(populateInputs({ ...inputs, stateIncentive: val }))
                }
              />
              <InputDescription>
                Enter the estimated incentives amount from the state
              </InputDescription>
            </IncentiveInput>
            <IncentiveInput id="question-utilityIncentive">
              <InputPageQuestionText>
                Utility Incentive Amount
              </InputPageQuestionText>
              <InputText
                id="utilityIncentive"
                prefix={'$'}
                value={inputs.utilityIncentive}
                onChange={(val) =>
                  dispatch(populateInputs({ ...inputs, utilityIncentive: val }))
                }
              />
              <InputDescription>
                Enter the estimated incentives amount from the utility
              </InputDescription>
            </IncentiveInput>
          </IncentiveInputContainer>
        </IncentivesSection>

        <MobileValidationSection
          $show={hasReachedBottom && !validInputs}
          $submitVisible={validInputs || isAtBottom}
          onClick={handleValidationClick}
        >
          <ValidationTooltipTitle>
            Please complete the following:
          </ValidationTooltipTitle>
          <ValidationTooltipList>
            {invalidMessages.map((message, index) => (
              <ValidationTooltipItem key={index}>
                {message}
              </ValidationTooltipItem>
            ))}
          </ValidationTooltipList>
        </MobileValidationSection>

        <SubmitButtonWrapper
          $column={1}
          $hideOnMobile={!validInputs && !isAtBottom}
          onMouseEnter={() => !validInputs && setShowValidationTooltip(true)}
          onMouseLeave={() => setShowValidationTooltip(false)}
        >
          <SubmitButton
            onClick={() => submit()}
            disabled={!validInputs || isSubmitting}
          >
            {' '}
            {isSubmitting ? 'Submitting...' : 'Submit'}{' '}
          </SubmitButton>
          <ValidationTooltip $show={showValidationTooltip && !validInputs}>
            <ValidationTooltipTitle>
              Please complete the following:
            </ValidationTooltipTitle>
            <ValidationTooltipList>
              {invalidMessages.map((message, index) => (
                <ValidationTooltipItem key={index}>
                  {message}
                </ValidationTooltipItem>
              ))}
            </ValidationTooltipList>
          </ValidationTooltip>
        </SubmitButtonWrapper>
      </InputPageWrapper>
    </PageLayout>
  );
};

InputPage.propTypes = {};

export default InputPage;
